<script lang="ts" setup>
import type { EsProductFragment } from '#graphql-operations'
import type { DataLayerItem } from '~layers/composables/useDataLayer'
import { AppLink } from '#components'

type showOptionsType = {
  wishlist?: boolean
  colorVariants?: boolean
  labels?: boolean
  addToCart?: boolean
}

const props = withDefaults(defineProps<{
  product: EsProductFragment
  list?: string
  parameter?: string
  dataLayerOverrides?: Partial<DataLayerItem>
  showOptions?: showOptionsType
}>(), {
  list: 'other',
  parameter: '',
  dataLayerOverrides: undefined,
  showOptions: undefined,
})

const { product } = toRefs(props)

const showOptions = computed(() => ({
  wishlist: true,
  colorVariants: true,
  labels: true,
  addToCart: false,
  ...props.showOptions,
}))

const hover = ref(false)

const imageSrc = computed(() => {
  let imageSrc = product.value.image

  if (hover.value && product.value.hoverImage) {
    imageSrc = product.value.hoverImage
  }

  return useXcdnImage(imageSrc, 'f=rs:fit:400:400', product.value.name)
})

const prices = computed(() => {
  return {
    regularPrice: product.value.regularPrice,
    finalPrice: product.value.finalPrice,
  }
})

function mouseOver() {
  hover.value = true
}

function mouseOut() {
  hover.value = false
}

const emit = defineEmits(['productClick'])
const { selectItem } = useDataLayer()

const trackProductClick = ({ product, dataLayerOverrides }: { product: EsProductFragment, dataLayerOverrides: Record<string, unknown> }) => {
  selectItem(product, dataLayerOverrides)
  emit('productClick', product)
}
</script>

<template>
  <article
    v-if="product"
    class="product-item"
  >
    <div class="product-item__details">
      <!-- <ProductReviewStars
        :rating="5"
        class="product-item__rating"
      /> -->
      <div class="product-item__name">
        <AppLink
          :to="`${product.url}${parameter}`"
          class="product-item__link"
          :class="{ 'sqzl-click': product?.squeezely }"
          :data-sqzl-personalization-id="product?.squeezely?.personalizationId"
          :data-sqzl-variant-id="product?.squeezely?.variantId"
          :data-sqzl-variant-revision="product?.squeezely?.variantRevision"
          @click="trackProductClick({ product: product, dataLayerOverrides: dataLayerOverrides })"
          @mouseover="mouseOver"
          @mouseout="mouseOut"
        >
          {{ product.mainTitle }}
        </AppLink>
        <span class="product-item__brand">
          {{ product.subTitle }}
        </span>
      </div>

      <UiPrice
        class="product-item__price"
        :prices="prices"
      />

      <div
        v-if="showOptions.labels"
        class="product-item__delivery-promise"
      >
        <ProductLabelDeliveryPromise :product="product" />
      </div>

      <div
        v-if="showOptions.colorVariants"
        class="product-item__color-variants-wrapper"
      >
        <div
          v-if="product.hasVariants"
          class="product-item__color-variants"
        >
          {{ $t('More variants available') }}
        </div>
      </div>

      <slot name="additional-details" />
    </div>

    <div
      v-if="showOptions.labels"
      class="product-item__labels"
    >
      <ProductLabelSale :product="product" />
      <ProductLabelMarketing :product="product" />
    </div>

    <ProductToggleWishlist
      v-if="showOptions.wishlist"
      class="product-item__wishlist"
      :product="product"
    />

    <slot name="additional" />

    <div class="product-item__visual">
      <img
        class="product-item__image"
        :src="imageSrc"
        width="400"
        height="400"
        loading="lazy"
        :alt="product.name"
      >
    </div>

    <!-- <pre>{{ product }}</pre> -->
  </article>
</template>

<style>
@import url("~layers/components/Product/Tile.css");
</style>
